import { useMemo } from "react"

import { useQuery } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { Button, Divider, Label, MenuItem, Message, Tab, TabPane } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"

import api, { asQuery } from "api"
import { useAppSelector } from "store"

import YourAssessmentRound from "./YourAssessmentRound"

type IChallengeYourAssessments = {
  challenge: OpenAPI.Schemas["Challenge"] & OpenAPI.Schemas["AssignChallengeAssessmentDetail"]
  demoingTour?: string
}

function ChallengeYourAssessments({ challenge, demoingTour }: IChallengeYourAssessments) {
  const user = useAppSelector((state) => state.user)

  const assessmentRounds = challenge.ownerIdeaTemplate?.assessments?.rounds || []

  const assignedAssessmentQuery = useQuery({
    queryKey: ["challenges", challenge?._id, "assessments"],
    queryFn: asQuery(api.challenges.getAssignedAssessments, {
      params: [challenge?._id],
    }),
    enabled: !!user?._id,
  })
  const assignedAssessmentIdeas = assignedAssessmentQuery.data?.ideas || []

  const assignmentCounts = useMemo(() => {
    const assessmentsPerRound = {}
    assignedAssessmentIdeas.forEach((idea) => {
      idea.assessments.forEach((assessment) => {
        if (assessment.user !== user?._id) return
        if (assessment.isSubmitted) return
        if (!assessmentsPerRound[assessment.assessmentRoundId]) {
          assessmentsPerRound[assessment.assessmentRoundId] = 0
        }
        assessmentsPerRound[assessment.assessmentRoundId] += 1
      })
    })
    return assessmentsPerRound
  }, [user?._id, assignedAssessmentIdeas])

  const { t } = useTranslation()

  return (
    <>
      {!user ? (
        <Message>
          <h3>Please login to view this page</h3>
          <p>Register or login to your SimplyDo account to view your assessments for this {t("generic.challenge")}.</p>
          <Divider section />
          <Button as={Link} to={`/login?then=/challenges/${challenge?._id}/assessments`} primary>
            Login or register
          </Button>
        </Message>
      ) : (
        <Tab
          panes={assessmentRounds.map((round) => ({
            menuItem: (
              <MenuItem key={round.id}>
                {round.name}
                {assignmentCounts[round.id] ? (
                  <Label circular color="red" size="mini">
                    {assignmentCounts[round.id]}
                  </Label>
                ) : null}
              </MenuItem>
            ),
            key: round.id,
            render: () => (
              <TabPane key={round.id}>
                <YourAssessmentRound assessmentRound={round} challenge={challenge} demoingTour={demoingTour} />
              </TabPane>
            ),
          }))}
        />
      )}
    </>
  )
}

export default ChallengeYourAssessments
